import React, { useMemo, useRef, useState } from 'react'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { BrandKitsAPI, GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { Close8, Plus16 } from '@makyoapp/makyo-icon/icon'

import { useBrandKitDetailContext } from '@/context/BrandKitDetailContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useReqError } from '@/hooks/useReqError'

import { ScrollMenu, ScrollMenuItem } from '../ScrollMenu'

export function BrandKitLogo({ withPadding = true }: { withPadding?: boolean }) {
  const { brand, updateBrandDetail } = useBrandKitDetailContext()
  const printAPIError = useReqError()
  const canEditBrandKit = useOrgPerm(OrgPermission.BrandKitEdit)

  const [file, setFile] = useState<{
    file?: File
    src?: string
  }>({})

  const imgRef = useRef(null)

  const logos = useMemo(() => {
    return brand?.logos
      ? [...brand.logos, ...(canEditBrandKit ? ['#src#', '#add#'] : [])]
      : canEditBrandKit
        ? ['#src#', '#add#']
        : []
  }, [brand?.logos, canEditBrandKit])

  const [isLoading, setIsLoading] = useBoolean(false)

  const handleImageLoadError = (e: React.SyntheticEvent<HTMLImageElement, Event>, logoUrl: string) => {
    const imgElement = e.target as HTMLImageElement
    setTimeout(() => {
      imgElement.src = logoUrl // Retry loading the image after a short delay
    }, 1000)
  }

  return (
    <div className='w-full overflow-hidden'>
      <ScrollMenu className={clsx('flex-c py-2', logos.length > 0 && withPadding && 'px-8 py-4')}>
        {logos.map((logo, i) => {
          if (logo === '#add#') {
            return (
              <ScrollMenuItem key={i} itemId={`${i}`}>
                <div className='w-[100px]'>
                  <label
                    className={clsx(
                      'mk-bk-logo-item cursor-pointer gap-2',
                      isLoading && 'disabled opacity-10 cursor-block'
                    )}
                  >
                    <Plus16 />
                    <input
                      type='file'
                      className='sr-only'
                      accept='.jpeg,.jpg,.png,.webm'
                      disabled={isLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file && brand) {
                          const split = file.name.split('.')
                          const ext = split[split.length - 1]
                          setIsLoading(true)
                          setFile({
                            file,
                            src: URL.createObjectURL(file),
                          })
                          BrandKitsAPI.ReqUploadLogo(brand.id, file, ext)
                            .then((res) => {
                              // add a slight delay before attempting to render the newly uploaded image due to
                              // the image wasn't fully processed on the server when the URL is used in the frontend (logs showing 404 (Not Found))
                              setTimeout(() => {
                                updateBrandDetail({
                                  ...brand,
                                  logos: [...brand.logos, res._File],
                                })
                              }, 1000)
                            })
                            .catch(printAPIError)
                            .finally(() => {
                              setFile({})
                              setIsLoading(false)
                            })
                        }
                      }}
                    />
                  </label>
                </div>
              </ScrollMenuItem>
            )
          } else if (logo === '#src#') {
            return file.src ? (
              <ScrollMenuItem key={i} itemId={`${i}`}>
                <div className='mk-bk-logo-item relative'>
                  {isLoading && (
                    <div className='absolute inset-0 flex-cc bg-[rgba(0,0,0,0.2)]'>
                      <span className='loading spin' />
                    </div>
                  )}
                  <img ref={imgRef} src={file.src} onLoad={() => file.src && URL.revokeObjectURL(file.src)} />
                </div>
              </ScrollMenuItem>
            ) : (
              <ScrollMenuItem key={i} itemId={`${i}`}></ScrollMenuItem>
            )
          } else {
            const link = GetImageMetdataLink(logo, ImageMetadata.XS)
            return (
              <ScrollMenuItem key={i} itemId={`${i}`}>
                <div className='mk-bk-logo-item relative'>
                  <img src={link} onError={(e) => handleImageLoadError(e, link)} /* Retry if error occurs */ />
                  {canEditBrandKit && (
                    <button
                      id={`logo-delete-${i}`}
                      className='mk-bk-logo-delete'
                      onClick={() => {
                        if (brand) {
                          BrandKitsAPI.ReqDeleteLogo(brand.id, {
                            _File: logo,
                          })
                            .then(() => {
                              updateBrandDetail({
                                ...brand,
                                logos: brand.logos.filter((l) => l !== logo),
                              })
                            })
                            .catch(printAPIError)
                        }
                      }}
                    >
                      <Close8 />
                    </button>
                  )}
                </div>
              </ScrollMenuItem>
            )
          }
        })}
      </ScrollMenu>
    </div>
  )
}
